import React, {CSSProperties, JSX} from 'react';
import XMLViewer from 'react-xml-viewer';
import styles from './_dealer-service-history-listview.module.scss';
import Typography from '@mui/material/Typography';
import { Box, Card, CardContent, CardHeader, Collapse, IconButton } from '@mui/material';
import { IAcknowledgementsReceived, IApplicationsReceived, CommentsReceived, ICommentsSent, IDecisionsSent, IOrderableType, DocumentsReceivedAcknowledgementSent } from '../../../Interfaces/Common/IDealerServiceAppHistory';
import { Constants } from '../../../utils/constants';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import {getStringValue} from "../../../utils/utils";
import { toDollars } from '@amsanalytics/react-shared-library';

interface DealerServiceHistoryProps {
  itemStyle?: CSSProperties;
  appSource?: string;
  historyItems: IOrderableType[];
}

export default function DealerServiceHistoryListView(props: DealerServiceHistoryProps) {
  return (
    <>
      <div className={styles.dealerServiceHistoryListViewContainer}>
        {props.historyItems.map((hi, key) => {
          return (
            <React.Fragment key={key}>
              {hi.typeId === Constants.dealerServiceHistoryTypes.applicationReceived && (
                <ApplicationsReceivedCard
                  applicationReceived={hi as IApplicationsReceived}
                  appSource={props.appSource}
                />
              )}
              {hi.typeId === Constants.dealerServiceHistoryTypes.acknowledgementReceived && (
                <AcknowledgementsReceivedCard
                  acknowledgementReceived={hi as IAcknowledgementsReceived}
                />
              )}
              {hi.typeId === Constants.dealerServiceHistoryTypes.documentsReceivedAcknowledgementSent && (
                <DocumentsReceivedAcknowledgementSentCard
                  documentsReceivedAcknowledgementSent={hi as DocumentsReceivedAcknowledgementSent}
                />
              )}

              {hi.typeId === Constants.dealerServiceHistoryTypes.commentsReceived && (
                <CommentsReceivedCard
                  commentsReceived={hi as CommentsReceived}
                />
              )}
              {hi.typeId === Constants.dealerServiceHistoryTypes.decisionSent && (
                <DecisionsSentCard
                  decisionsSent={hi as IDecisionsSent}
                />
              )}
              {hi.typeId === Constants.dealerServiceHistoryTypes.commentsSent && (
                <CommentsSentCard
                  commentsSent={hi as ICommentsSent}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

function DealerServiceHistoryHeader(props: { historyItem: IOrderableType, avatar: JSX.Element }) {
  return (
    <CardHeader
      avatar={props.avatar}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography style={{ fontWeight: "bold" }} variant="subtitle1">
            {props.historyItem.title}
          </Typography>
          <Typography variant="caption">
            {new Date(props.historyItem.createdDate).toLocaleString()}
          </Typography>
        </div>
      }
    />
  );
}

function DealerServiceHistoryDataPoint(props: { label: string; value: string | Date | number | undefined }) {
  return (
    <div style={{ width: '200px' }}>
      <Typography variant="caption" className={styles.boldFont}>
        {`${props.label}: `}
      </Typography>
      <Typography variant="caption" className={styles.leftSpacer}>
        {getStringValue(props.value)}
      </Typography>
    </div>
  );
}

function DealerServiceHistorySingleLine(props: { label: string; text: string; }) {
  return (
    <>
      <div className={styles.wrappedContent}>
        <Typography variant="caption" className={styles.boldFont}>
          {`${props.label}: `}
        </Typography>
        <Typography variant="caption" className={styles.leftSpacer}>
          {props.text}
        </Typography>
      </div>
    </>
  );
}

export function CardRawDataViewer(props: { rawData: string, type: "XML" | "JSON" }) {
  const [open, setOpen] = React.useState(false);
  const rawData = props.type === "JSON"
    ? props.rawData.replaceAll(',"', ', "') // Sort of a fake word wrapping to keep it narrower
    : props.rawData;

  return (
    <div style={{ width: '100%', maxWidth: '500px' }}>
      <IconButton aria-label='expand card' size='small' onClick={() => setOpen(!open)}>
        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <Box margin={1}>
          <Typography variant='h6' gutterBottom component='div' style={{ display: "flex" }}>
            Raw Data
          </Typography>
          {props.type === "JSON"
            ?
            <div className={styles.rawDataContent}>{rawData}</div>
            :
            <XMLViewer xml={rawData} />
          }
        </Box>
      </Collapse>
    </div>
  );
}

function ApplicationsReceivedCard(props: { applicationReceived: IApplicationsReceived, appSource?: string }) {
  
  return (
    <Card style={{ marginBottom: "5px" }}>
      <DealerServiceHistoryHeader historyItem={props.applicationReceived}
        avatar={<CallReceivedIcon fontSize="small" />} />
      <CardContent className={styles.wrappedContent}>
        {props.applicationReceived.lenderDealerId && (
          <DealerServiceHistoryDataPoint label="Dealer" value={props.applicationReceived.lenderDealerId} />
        )}
        {props.applicationReceived.dealerUser && (
          <DealerServiceHistoryDataPoint label="Dealer User" value={props.applicationReceived.dealerUser} />
        )}
        {props.applicationReceived.lenderProgram && (
          <DealerServiceHistoryDataPoint label="Lender Program" value={props.applicationReceived.lenderProgram} />
        )}
        {!!props.applicationReceived.estimatedAmountFinanced && (
          <DealerServiceHistoryDataPoint label="Amount to Finance" value={toDollars(props.applicationReceived.estimatedAmountFinanced)} />
        )}
        {!!props.applicationReceived.term && (
          <DealerServiceHistoryDataPoint label="Term (months)" value={props.applicationReceived.term} />
        )}
        {!!props.applicationReceived.apr && (
          <DealerServiceHistoryDataPoint label="Requested APR" value={props.applicationReceived.apr} />
        )}
        {!!props.applicationReceived.cashDown && (
          <DealerServiceHistoryDataPoint label="Cash Down" value={toDollars(props.applicationReceived.cashDown)} />
        )}
        {!!props.applicationReceived.cashPrice && (
          <DealerServiceHistoryDataPoint label="Cash Selling Price" value={toDollars(props.applicationReceived.cashPrice)} />
        )}
        {!!props.applicationReceived.rebate && (
          <DealerServiceHistoryDataPoint label="Rebate" value={toDollars(props.applicationReceived.rebate)} />
        )}
        {!!props.applicationReceived.netTrade && (
          <DealerServiceHistoryDataPoint label="Net Trade" value={toDollars(props.applicationReceived.netTrade)} />
        )}
        {!!props.applicationReceived.taxTitleAndLicense && (
          <DealerServiceHistoryDataPoint label="TT&amp;L" value={toDollars(props.applicationReceived.taxTitleAndLicense)} />
        )}
        {!!props.applicationReceived.creditLife && (
          <DealerServiceHistoryDataPoint label="Credit Life Ins" value={toDollars(props.applicationReceived.creditLife)} />
        )}
        {!!props.applicationReceived.accHealthInsurance && (
          <DealerServiceHistoryDataPoint label="Acc/Health Ins" value={toDollars(props.applicationReceived.accHealthInsurance)} />
        )}
        {!!props.applicationReceived.frontEndFees && (
          <DealerServiceHistoryDataPoint label="Front End Fees" value={toDollars(props.applicationReceived.frontEndFees)} />
        )}
        {!!props.applicationReceived.backEndFees && (
          <DealerServiceHistoryDataPoint label="Back End Fees" value={toDollars(props.applicationReceived.backEndFees)} />
        )}
        {!!props.applicationReceived.warranty && (
          <DealerServiceHistoryDataPoint label="Warranty" value={toDollars(props.applicationReceived.warranty)} />
        )}
        {!!props.applicationReceived.unpaidBalance && (
          <DealerServiceHistoryDataPoint label="Unpaid Bal" value={toDollars(props.applicationReceived.unpaidBalance)} />
        )}
        {!!props.applicationReceived.gap && (
          <DealerServiceHistoryDataPoint label="GAP" value={toDollars(props.applicationReceived.gap)} />
        )}
        {!!props.applicationReceived.taxes && (
          <DealerServiceHistoryDataPoint label="Sales Tax" value={toDollars(props.applicationReceived.taxes)} />
        )}
        {!!props.applicationReceived.bookYear && props.applicationReceived.bookYear > 0 && (
          <DealerServiceHistoryDataPoint label="Vehicle Year" value={props.applicationReceived.bookYear} />
        )}
        {props.applicationReceived.bookMake && (
          <DealerServiceHistoryDataPoint label="Vehicle Make" value={props.applicationReceived.bookMake} />
        )}
        {props.applicationReceived.model && (
          <DealerServiceHistoryDataPoint label="Vehicle Model" value={props.applicationReceived.model} />
        )}
        <CardRawDataViewer rawData={props.applicationReceived.rawXml} type={props.appSource?.toUpperCase() === "DEALER FLOW" ? "JSON" : "XML"} />
      </CardContent>
    </Card>
  );
}

function AcknowledgementsReceivedCard(props: { acknowledgementReceived: IAcknowledgementsReceived }) {
  return (
    <Card style={{ marginBottom: "5px" }}>
      <DealerServiceHistoryHeader historyItem={props.acknowledgementReceived}
        avatar={<CallReceivedIcon fontSize="small" />} />
      <CardContent className={styles.wrappedContent}>
        {props.acknowledgementReceived.type && (
          <div style={{ width: '100%' }}>
            <Typography variant="caption" className={styles.boldFont}>
              Acknowledgement Type:
            </Typography>
            <Typography variant="caption" className={styles.leftSpacer}>
              {(props.acknowledgementReceived.type === "ack") ? "Received"
                : (props.acknowledgementReceived.type === "error") ? "Error"
                  : (props.acknowledgementReceived.type === "resend") ? "Resend"
                    : props.acknowledgementReceived.type}
            </Typography>
          </div>
        )}
        {props.acknowledgementReceived.errorDescription && (
          <div style={{ width: '100%' }}>
            <Typography variant="caption" className={styles.boldFont}>
              Error Description:
            </Typography>
            <Typography variant="caption" className={styles.leftSpacer}>
              {props.acknowledgementReceived.errorDescription}
            </Typography>
          </div>
        )}
        <CardRawDataViewer rawData={props.acknowledgementReceived.rawXml} type={"XML"} />
      </CardContent>
    </Card>
  );
}

function DocumentsReceivedAcknowledgementSentCard(props: { documentsReceivedAcknowledgementSent: DocumentsReceivedAcknowledgementSent }) {
  return (
    <Card style={{ marginBottom: "5px" }}>
      <DealerServiceHistoryHeader historyItem={props.documentsReceivedAcknowledgementSent}
        avatar={<CallMadeIcon fontSize="small" />} />
      <CardContent className={styles.wrappedContent}>
        {props.documentsReceivedAcknowledgementSent.type && (
          <div style={{ width: '100%' }}>
            <Typography variant="caption" className={styles.boldFont}>
              Documents Received Acknowledgement Sent:
            </Typography>
            <Typography variant="caption" className={styles.leftSpacer}>
              {(props.documentsReceivedAcknowledgementSent.type === "ack") ? "Received"
                : (props.documentsReceivedAcknowledgementSent.type === "error") ? "Error"
                  : (props.documentsReceivedAcknowledgementSent.type === "resend") ? "Resend"
                    : props.documentsReceivedAcknowledgementSent.type}
            </Typography>
          </div>
        )}
        {props.documentsReceivedAcknowledgementSent.errorDescription && (
          <div style={{ width: '100%' }}>
            <Typography variant="caption" className={styles.boldFont}>
              Error Description:
            </Typography>
            <Typography variant="caption" className={styles.leftSpacer}>
              {props.documentsReceivedAcknowledgementSent.errorDescription}
            </Typography>
          </div>
        )}
        <CardRawDataViewer rawData={props.documentsReceivedAcknowledgementSent.rawXml} type={"XML"} />
      </CardContent>
    </Card>
  );
}

function CommentsReceivedCard(props: { commentsReceived: CommentsReceived }) {
  return (
    <Card style={{ marginBottom: "5px" }}>
      <DealerServiceHistoryHeader historyItem={props.commentsReceived}
        avatar={<CallReceivedIcon fontSize="small" />} />
      <CardContent className={styles.wrappedContent} style={{ flexDirection: 'column' }}>
        {props.commentsReceived.comments.comment.map((cr, key) => {
          return (
            <div className={styles.wrappedContent} key={key}>
              <DealerServiceHistorySingleLine label={"Comment"} text={cr.value} />
            </div>
          );
        })}
        <CardRawDataViewer rawData={props.commentsReceived.rawXml} type={"XML"} />
      </CardContent>
    </Card>
  );
}

function DecisionsSentCard(props: { decisionsSent: IDecisionsSent }) {
  return (
    <Card style={{ marginBottom: "5px" }}>
      <DealerServiceHistoryHeader historyItem={props.decisionsSent}
        avatar={<CallMadeIcon fontSize="small" />} />
      <CardContent className={styles.wrappedContent}>
        {props.decisionsSent.decisionCode && (
          <DealerServiceHistoryDataPoint label="Decision" value={props.decisionsSent.decisionCode} />
        )}
        {props.decisionsSent.tierLevel && (
          <DealerServiceHistoryDataPoint label="Pricing Program" value={props.decisionsSent.tierLevel} />
        )}
        {!!props.decisionsSent.amountToFinance && (
          <DealerServiceHistoryDataPoint label="Amount to Finance" value={toDollars(props.decisionsSent.amountToFinance)} />
        )}
        {!!props.decisionsSent.termInMonths && (
          <DealerServiceHistoryDataPoint label="Term (months)" value={props.decisionsSent.termInMonths} />
        )}
        {!!props.decisionsSent.payment && (
          <DealerServiceHistoryDataPoint label="Payment" value={toDollars(props.decisionsSent.payment)} />
        )}
        {!!props.decisionsSent.cashDown && (
          <DealerServiceHistoryDataPoint label="Cash Down" value={toDollars(props.decisionsSent.cashDown)} />
        )}
        {!!props.decisionsSent.buyRate && (
          <DealerServiceHistoryDataPoint label="Rate" value={props.decisionsSent.buyRate} />
        )}
        {!!props.decisionsSent.ltvRatio && (
          <DealerServiceHistoryDataPoint label="LTV" value={props.decisionsSent.ltvRatio} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {props.decisionsSent.analystComments && props.decisionsSent.analystComments.map((ac, key) => {
            return (
              <div key={key}>
                <DealerServiceHistorySingleLine label={"Comment"} text={ac.comments} />
              </div>
            );
          })
          }
          {props.decisionsSent.stipulations && props.decisionsSent.stipulations.map((st, key) => {
            return (
              <div key={key}>
                <DealerServiceHistorySingleLine label={"Stipulation"} text={st.text} />
              </div>
            );
          })
          }
          {props.decisionsSent.conditionedOrDeclinedReasons && props.decisionsSent.conditionedOrDeclinedReasons.map((dr, key) => {
            return (
              <div key={key}>
                <DealerServiceHistorySingleLine label={"Denial Reason"} text={dr.text} />
              </div>
            );
          })
          }
        </div>
        <CardRawDataViewer rawData={props.decisionsSent.rawXml} type={"XML"} />
      </CardContent>
    </Card>
  );
}

function CommentsSentCard(props: { commentsSent: ICommentsSent }) {
  return (
    <Card style={{ marginBottom: "5px" }}>
      <DealerServiceHistoryHeader
        historyItem={props.commentsSent}
        avatar={<CallMadeIcon fontSize="small" />} />
      <CardContent className={styles.wrappedContent} style={{ flexDirection: 'column' }}>
        {props.commentsSent.comments.map((cs, key) => {
          return (
            <div className={styles.wrappedContent} key={key}>
              <DealerServiceHistorySingleLine label={"Comment"} text={cs.commentsText} />
            </div>
          );
        })}
        <CardRawDataViewer rawData={props.commentsSent.rawXml} type={"XML"} />
      </CardContent>
    </Card>
  );
}
